import { MOTION_TYPES } from '../utils/motionTypes.js';

export const MotionStage = {
  MADE: 'MADE',
  SECONDED: 'SECONDED',
  STATED: 'STATED',
  DEBATE: 'DEBATE',
  PUT: 'PUT',
  TALLY: 'TALLY',
  ANNOUNCE: 'ANNOUNCE'
};

/**
 * Current app uses `motion.status` strings. This maps them onto canonical stages.
 * We will migrate UI/logic to canonical stages incrementally.
 */
export function getStageFromLegacyStatus(status) {
  switch (status) {
    case 'pending_second':
      return MotionStage.MADE;
    case 'seconded':
      return MotionStage.SECONDED;
    case 'stated':
      return MotionStage.STATED;
    case 'open_debate':
    case 'close_debate':
      return MotionStage.DEBATE;
    case 'ready_for_vote':
      return MotionStage.PUT;
    case 'voted_passed':
    case 'voted_failed':
      // Legacy code collapses "tally" into a binary result with no counts.
      return MotionStage.TALLY;
    case 'completed':
      return MotionStage.ANNOUNCE;
    default:
      return MotionStage.MADE;
  }
}

export const MotionEvent = {
  SECOND: 'SECOND',
  STATE: 'STATE',
  OPEN_DEBATE: 'OPEN_DEBATE',
  CLOSE_DEBATE: 'CLOSE_DEBATE',
  PUT: 'PUT',
  TALLY: 'TALLY',
  ANNOUNCE: 'ANNOUNCE'
};

/**
 * Validate that we only move forward through the 7-step rule.
 * Returns { ok: true, nextStatus } or { ok: false, reason }.
 * @param {string|null} motionTypeKey - e.g. MAIN, AMEND; used when chair "states" a motion.
 */
export function getNextLegacyStatus(currentStatus, event, motionTypeKey = null) {
  // After second: stating the question opens debate immediately (no separate "open debate" step).
  if (currentStatus === 'seconded' && event === MotionEvent.STATE) {
    const meta = motionTypeKey ? MOTION_TYPES[motionTypeKey] : null;
    // Branching flows (RONR_taxonomy.md): INFO/CHAIR motions are handled by the chair
    // immediately, with no debate and no amendment.
    if (meta?.flowType === 'INFO' || meta?.flowType === 'CHAIR') {
      return { ok: true, nextStatus: 'ready_for_vote' };
    }
    // Debatable motions open debate.
    if (meta && meta.debatable) {
      return { ok: true, nextStatus: 'open_debate' };
    }
    // Undebatable but amendable (e.g. Limit/Extend Debate, Recess, Fix the Hour):
    // hold the question at "stated" so a member may still offer an amendment before
    // the chair puts the question. RONR: no debate is allowed, but the motion is amendable.
    if (meta && !meta.debatable && meta.amendable) {
      return { ok: true, nextStatus: 'stated' };
    }
    // Undebatable and unamendable: proceed straight to the vote.
    return { ok: true, nextStatus: 'ready_for_vote' };
  }

  // Legacy status ordering (subset of canonical 7 steps)
  const allowed = {
    pending_second: { [MotionEvent.SECOND]: 'seconded' },
    stated: { [MotionEvent.OPEN_DEBATE]: 'open_debate', [MotionEvent.PUT]: 'ready_for_vote' },
    open_debate: { [MotionEvent.CLOSE_DEBATE]: 'close_debate' },
    close_debate: { [MotionEvent.PUT]: 'ready_for_vote' },
    ready_for_vote: { [MotionEvent.TALLY]: null }, // special-cased below
    voted_passed: { [MotionEvent.ANNOUNCE]: 'completed' },
    voted_failed: { [MotionEvent.ANNOUNCE]: 'completed' }
  };

  // TALLY in legacy code is recorded by setting voted_passed/voted_failed
  if (currentStatus === 'ready_for_vote' && event === MotionEvent.TALLY) {
    return { ok: true, nextStatus: '__TALLY__' };
  }

  const next = allowed[currentStatus]?.[event];
  if (!next) {
    return { ok: false, reason: `Invalid motion transition: ${currentStatus} -> ${event}` };
  }
  return { ok: true, nextStatus: next };
}

